import { z } from 'zod';

/**
 * Context files uploaded in wizard step 2 and fed to generation as extra
 * context (2026-08-26 design doc, D5-D8).
 *
 * The stored row keeps both the original bytes and the extracted text (D6):
 * the bytes so the source document can be re-extracted if a parser improves,
 * the text so generation never has to re-parse. Neither is exposed here —
 * this schema is the API surface, and shipping a 25 MB base64 blob or 200k
 * characters of prose in a list response would be pointless. The extracted
 * text reaches the model through the prompt, not through the client.
 */

/**
 * Accepted upload formats (D5). Extension is *not* trusted: the backend
 * sniffs magic bytes and rejects a mismatch, so a renamed binary cannot slip
 * through as `.txt` (D8).
 */
export const contextFileKindSchema = z.enum(['txt', 'md', 'pdf', 'docx', 'xlsx']);
export type ContextFileKind = z.infer<typeof contextFileKindSchema>;

/** Per-file upload ceiling (D7). Mirrored by nginx `client_max_body_size` and a Hono `bodyLimit`. */
export const CONTEXT_FILE_MAX_BYTES = 25 * 1024 * 1024;

/**
 * Ceiling on extracted text per file (D7).
 *
 * Since the two-level digest landed (D9), this governs what the *digest*
 * prompt ingests, not what generation ingests — gen/regen see digests
 * (~7.5k characters), never this.
 */
export const CONTEXT_FILE_MAX_TEXT_CHARS = 200_000;

export const contextFileSchema = z.object({
  id: z.string().uuid(),
  matrixId: z.string().uuid(),
  filename: z.string().min(1).max(255),
  kind: contextFileKindSchema,
  sizeBytes: z.number().int().nonnegative(),
  /** Characters of extracted text actually stored, after any truncation. */
  textChars: z.number().int().nonnegative(),
  /** True when extraction hit `CONTEXT_FILE_MAX_TEXT_CHARS` and the tail was dropped (D7). */
  truncated: z.boolean(),
  createdAt: z.string().datetime(),
  createdBy: z.string().uuid().nullable(),
});
export type ContextFile = z.infer<typeof contextFileSchema>;

/** `GET /matrices/:matrixId/context-files` */
export const contextFileListSchema = z.array(contextFileSchema);

/**
 * Why an upload was rejected (D8). Sent as the API error `code` so the
 * frontend can render the matching French message without parsing prose.
 */
export const contextFileRejectionSchema = z.enum([
  /** Nothing usable came out of the file (an empty sheet or document). */
  'PDF_NO_EXTRACTABLE_TEXT',
  /** A PDF, refused whatever it contains: its text is too often badly extracted (#20). */
  'PDF_NOT_ACCEPTED',
  /** Password-protected or otherwise encrypted. */
  'FILE_ENCRYPTED',
  /** Parser could not read the file at all. */
  'FILE_UNREADABLE',
  /** Magic bytes contradict the extension, or a "text" file is not text. */
  'FILE_BINARY_CONTENT',
  /** Extension outside `contextFileKindSchema`. */
  'FILE_TYPE_UNSUPPORTED',
  /** Larger than `CONTEXT_FILE_MAX_BYTES`. */
  'FILE_TOO_LARGE',
]);
export type ContextFileRejection = z.infer<typeof contextFileRejectionSchema>;
